import { useState, useEffect } from 'react';

const UserMatchesTable = ({ 
    userMatches, 
    onSelectUser, 
    inFlight, 
    pageSize = 15 
}) => {
    const [page, setPage] = useState(1);
    const [isResultsCollapsed, setIsResultsCollapsed] = useState(false);

    const totalMatches = userMatches.length;
    const totalPages = totalMatches > 0 ? Math.ceil(totalMatches / pageSize) : 1;
    const startIndex = (page - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalMatches);
    const pagedMatches = userMatches.slice(startIndex, endIndex);

    // Reset page when userMatches changes
    useEffect(() => {
        setPage(1);
    }, [userMatches]);


    const renderPaginationControls = () => {
        if (totalPages <= 1) return null;

        return (
            <nav className="flex items-center gap-2 mb-4" aria-label="Pagination" role="navigation">
                <button
                    className="px-2 py-1 border rounded disabled:opacity-50"
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                >
                    First
                </button>
                <button
                    className="px-2 py-1 border rounded disabled:opacity-50"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                >
                    Previous
                </button>
                <span className="text-gray-700">
                    Page {page} of {totalPages}
                </span>
                <button
                    className="px-2 py-1 border rounded disabled:opacity-50"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                >
                    Next
                </button>
                <button
                    className="px-2 py-1 border rounded disabled:opacity-50"
                    onClick={() => setPage(totalPages)}
                    disabled={page === totalPages}
                >
                    Last
                </button>
            </nav>
        );
    };

    const renderResultsCount = () => {
        if (totalMatches <= 1) return null;

        return (
            <div className="font-medium text-gray-800 mb-2">
                {totalMatches === 100 ? (
                    <span>
                        More than 100 users found; only the first 100 results are returned.
                        Showing {startIndex + 1}-{endIndex}.
                    </span>
                ) : (
                    <span>
                        {totalMatches} users found. Showing {startIndex + 1}-{endIndex}.
                    </span>
                )}
            </div>
        );
    };

    const renderTable = () => {
        if (userMatches.length === 0) {
            return <div className="text-gray-700">No users found.</div>;
        }

        return (
            <>
                {renderResultsCount()}
                <div className="overflow-x-auto mb-4">
                    <table className="min-w-full border border-gray-300 bg-white rounded-lg">
                        <thead className="bg-gray-100">
                            <tr>
                                <th scope="col" className="px-4 py-2 text-left border-b">Name</th>
                                <th scope="col" className="px-4 py-2 text-left border-b">Username</th>
                                <th scope="col" className="px-4 py-2 text-left border-b">Cobrand</th>
                                <th scope="col" className="px-4 py-2 text-left border-b">Unit Name</th>
                                <th scope="col" className="px-4 py-2 text-left border-b">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pagedMatches.map((user) => {
                                return (
                                    <tr key={user.username} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 border-b">{`${user.firstName} ${user.lastName}`}</td>
                                        <td className="px-4 py-2 border-b">{user.username}</td>
                                        <td className="px-4 py-2 border-b">{user.cobrandName}</td>
                                        <td className="px-4 py-2 border-b">{user.unitName}</td>
                                        <td className="px-4 py-2 border-b">
                                            <button
                                                className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                                onClick={() => onSelectUser(user)}
                                                disabled={inFlight}
                                            >
                                                View report
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {renderPaginationControls()}
            </>
        );
    };

    return (
        <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-gray-800">User matches</div>
                <button
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 shadow focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                    onClick={() => setIsResultsCollapsed((v) => !v)}
                >
                    {isResultsCollapsed ? 'Show user matches' : 'Hide user matches'}
                </button>
            </div>
            {!isResultsCollapsed && renderTable()}
        </div>
    );
};

export default UserMatchesTable;